import React, { createContext, useContext, useEffect, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { configService } from '../services/configService';

interface WebSocketContextType {
  socket: Socket | null;
  connected: boolean;
  serverLogsUpdate: string | null;
  smsLogsUpdate: {type: string, message: string} | null;
  lockedAccountsUpdate: {type: string, data: any, timestamp: number} | null;
  clearServerLogsUpdate: () => void;
  clearSmsLogsUpdate: () => void;
  clearLockedAccountsUpdate: () => void;
}

const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  connected: false,
  serverLogsUpdate: null,
  smsLogsUpdate: null,
  lockedAccountsUpdate: null,
  clearServerLogsUpdate: () => {},
  clearSmsLogsUpdate: () => {},
  clearLockedAccountsUpdate: () => {},
});

export const useWebSocket = () => useContext(WebSocketContext);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [serverLogsUpdate, setServerLogsUpdate] = useState<string | null>(null);
  const [smsLogsUpdate, setSmsLogsUpdate] = useState<{type: string, message: string} | null>(null);
  const [lockedAccountsUpdate, setLockedAccountsUpdate] = useState<{type: string, data: any, timestamp: number} | null>(null);
  const { user, selectedCompany } = useAuth();

  useEffect(() => {
    // Get WebSocket URL from centralized config service
    const webSocketUrl = configService.getApiBaseUrl();

    // Initialize socket connection
    const newSocket = io(webSocketUrl, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      setConnected(false);
    });

    // Listen for server logs updates
    newSocket.on('server-logs-update', (data) => {
      setServerLogsUpdate(data);
    });

    // Listen for SMS logs updates
    newSocket.on('sms-logs-update', (data) => {
      setSmsLogsUpdate(data);
    });

    // Listen for locked account updates (admin only)
    newSocket.on('account-locked', (data) => {
      setLockedAccountsUpdate({ type: 'account-locked', data, timestamp: Date.now() });
    });

    newSocket.on('account-unlocked', (data) => {
      setLockedAccountsUpdate({ type: 'account-unlocked', data, timestamp: Date.now() });
    });

    newSocket.on('account-auto-unlocked', (data) => {
      setLockedAccountsUpdate({ type: 'account-auto-unlocked', data, timestamp: Date.now() });
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
  }, []); // Only run once since config is loaded at startup

  // Handle room subscriptions in a single useEffect
  useEffect(() => {
    if (socket && connected) {
      // Leave any existing rooms first
      if (selectedCompany?.id) {
        socket.emit('leave-company', selectedCompany.id);
      }
      if (user?.role === 'admin' || user?.role === 'superadmin') {
        socket.emit('leave-company', 'admin-channel');
      }

      // Join new rooms
      if (selectedCompany?.id) {
        socket.emit('join-company', selectedCompany.id);
      }
      if (user?.role === 'admin' || user?.role === 'superadmin') {
        socket.emit('join-company', 'admin-channel');
      }
    }
  }, [socket, connected, selectedCompany?.id, user?.role]);

  const clearServerLogsUpdate = () => {
    setServerLogsUpdate(null);
  };

  const clearSmsLogsUpdate = () => {
    setSmsLogsUpdate(null);
  };

  const clearLockedAccountsUpdate = () => {
    setLockedAccountsUpdate(null);
  };

  return (
    <WebSocketContext.Provider value={{ 
      socket, 
      connected,
      serverLogsUpdate,
      smsLogsUpdate,
      lockedAccountsUpdate,
      clearServerLogsUpdate,
      clearSmsLogsUpdate,
      clearLockedAccountsUpdate
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};
